// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// VIEW 5 — "Still open".
//
// One thin rule per live matter on a real calendar axis, banded by the stage the
// register says it has reached. The elapsed time IS the finding here, so this is
// a per-matter timeline rather than a funnel: nothing is aggregated away.
//
// TWO THINGS THIS FILE REFUSES TO DO.
//
//   1. SURVIVORSHIP. These two registers hold only what is OPEN today. A matter
//      that has been determined has left them, and the determination register
//      carries no lodgement date at all — so a true lodged-to-determined
//      duration cannot be computed from these files, and this site does not
//      invent one. Everything measured here is AGE.
//
//   2. THE REGISTER IS A SUBSET OF THE SCHEDULE. matters.json holds 285 rows for
//      189 distinct matters: the 96 claims on the Register of Native Title
//      Claims appear a second time on the Schedule of Applications, with
//      identical dates. Rendering the raw rows would render those 96 matters
//      twice and inflate every count, so rows are merged by Tribunal file number
//      first and each matter appears exactly once. The merged split — Claimant
//      130, Non-Claimant 52, Compensation 5, Revised 2 — is the honest cohort.

import { PENDING_STROKE, UNRECORDED, mixHex } from '../theme';
import { el, chart, defs, UNRECORDED_FILL } from '../components/svg';
import { esc, num, km2, pct, longDate, duration, tip, tipAttr, plural } from '../format';
import { glossTerm, glossIcon } from '../components/glossary';
import { openOverlay, closeButton } from '../overlay';
import { setParams, currentRoute } from '../router';
import { attachSvgZoom, type SvgZoomHandle } from '../utils/svgZoom';
import { getMeta, loadMatters, loadJurisdictions } from '../data';
import type { Matter } from '../types';

/* ───────────────────────────── the date chain ──────────────────────────── */

const DAY = 86_400_000;

type AnchorKey = 'lodged' | 'registeredOn' | 'notificationClosed' | 'courtOrder';

const ANCHORS: { key: AnchorKey; label: string }[] = [
  { key: 'lodged', label: 'Lodged with the Federal Court' },
  { key: 'registeredOn', label: 'Entered on the Register of Native Title Claims' },
  { key: 'notificationClosed', label: 'Notification period closed' },
  { key: 'courtOrder', label: 'Most recent Federal Court order' },
];

/** The three intervals between the four anchors, in the Act's own language. */
const STAGES: { title: string; act: string }[] = [
  {
    title: 'Lodgement to registration',
    act: 'From the day the application was filed with the Federal Court to the day the claim was entered on the Register of Native Title Claims — the section 190A registration test.',
  },
  {
    title: 'The notification period',
    act: 'From entry on the Register of Native Title Claims to the close of the notification period, during which anyone whose interests may be affected can seek to become a party (section 66).',
  },
  {
    title: 'Since notification closed',
    act: 'From the close of the notification period to the most recent order the Federal Court has made in the matter.',
  },
];

/**
 * One hue at three luminances. Sequence, not score: these are procedural stages
 * of a live matter, and nothing on this page may look like a finding. Method is
 * never a hue on this site, and neither is anything else that could be mistaken
 * for one.
 */
const STAGE_INK = [
  mixHex(PENDING_STROKE, '#ffffff', 0.55),
  mixHex(PENDING_STROKE, '#ffffff', 0.32),
  mixHex(PENDING_STROKE, '#ffffff', 0.1),
];

function msOf(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(`${iso}T00:00:00Z`);
  return Number.isFinite(t) ? t : null;
}

function dayCount(a: number, b: number): number {
  return Math.round((b - a) / DAY);
}

/** "331 days (11 months)" — the exact count first, the human span after it. */
function span(days: number): string {
  const d = plural(days, 'day');
  return days >= 60 ? `${d} (${duration(days)})` : d;
}

/* ─────────────────────────── merging the two files ─────────────────────── */

function pickField<K extends keyof Matter>(base: Matter, other: Matter, k: K): Matter[K] {
  return base[k] == null ? other[k] : base[k];
}

/**
 * One row per distinct matter. The Schedule of Applications is the complete list
 * of live matters and the Register of Native Title Claims is a subset of it, so
 * the schedule row is the base and register membership is carried across.
 */
function mergeMatters(rows: Matter[]): Matter[] {
  const byId = new Map<string, Matter>();
  for (const row of rows) {
    const prev = byId.get(row.id);
    if (!prev) { byId.set(row.id, { ...row }); continue; }
    const base = prev.onRegister && !row.onRegister ? row : prev;
    const other = base === prev ? row : prev;
    byId.set(row.id, {
      ...base,
      onRegister: prev.onRegister || row.onRegister,
      name: pickField(base, other, 'name'),
      lodged: pickField(base, other, 'lodged'),
      status: pickField(base, other, 'status'),
      registrationStatus: pickField(base, other, 'registrationStatus'),
      registeredOn: pickField(base, other, 'registeredOn'),
      notificationClosed: pickField(base, other, 'notificationClosed'),
      courtOrder: pickField(base, other, 'courtOrder'),
      representative: pickField(base, other, 'representative'),
      applicationType: pickField(base, other, 'applicationType'),
      areaSqKm: pickField(base, other, 'areaSqKm'),
      daysToRegister: pickField(base, other, 'daysToRegister'),
      daysToNotificationClose: pickField(base, other, 'daysToNotificationClose'),
      daysToCourtOrder: pickField(base, other, 'daysToCourtOrder'),
      daysSinceLodged: pickField(base, other, 'daysSinceLodged'),
    });
  }
  return [...byId.values()];
}

/* ─────────────────────────────── the bands ─────────────────────────────── */

const REGISTER_BAND = 'register';

const REGISTER_PLAIN = 'These claims have met the section 190A registration test and are entered on the Register of Native Title Claims. Registration carries procedural rights — most importantly the right to negotiate about mining and other future acts. They also sit on the Schedule of Applications: the Register is a subset of the Schedule, not a separate list.';

const STATUS_ORDER = [
  'Currently identified for Reg. Decision (new decision in progress - s 190A)',
  'Not currently identified for Reg. Decision',
  'Not Accepted for registration',
  'Not Accepted for registration (new decision in progress - s 190A)',
  'Not Accepted for registration (new decision in progress - s 190E reconsideration)',
];

const STATUS_PLAIN: Record<string, string> = {
  'Accepted for registration':
    'The claim met the section 190A threshold and is entered on the Register of Native Title Claims.',
  'Accepted for registration (new decision in progress - s 190A)':
    'The claim is on the Register of Native Title Claims and a fresh registration decision under section 190A is in progress.',
  'Currently identified for Reg. Decision (new decision in progress - s 190A)':
    'A registration decision under section 190A is in progress right now.',
  'Not currently identified for Reg. Decision':
    'No registration decision is under way. Every matter here is a non-claimant, compensation or revised determination application — the application types the section 190A registration test is not applied to at all.',
  'Not Accepted for registration':
    'The application did not meet the section 190A threshold at the time it was tested. It remains before the Court, it can be amended, and it can be tested again. It is a threshold, not a verdict.',
  'Not Accepted for registration (new decision in progress - s 190A)':
    'The application did not meet the section 190A threshold when it was last tested, and a fresh registration decision is in progress.',
  'Not Accepted for registration (new decision in progress - s 190E reconsideration)':
    'The application did not meet the section 190A threshold, and the Tribunal is reconsidering that decision under section 190E.',
};

interface Band { key: string; label: string; plain: string; rows: Matter[] }

function buildBands(rows: Matter[]): Band[] {
  const onRegister = rows.filter((m) => m.onRegister);
  const rest = rows.filter((m) => !m.onRegister);

  const byStatus = new Map<string, Matter[]>();
  for (const m of rest) {
    const k = m.registrationStatus ?? '';
    if (!byStatus.has(k)) byStatus.set(k, []);
    byStatus.get(k)!.push(m);
  }

  const known = STATUS_ORDER.filter((s) => byStatus.has(s));
  const extra = [...byStatus.keys()]
    .filter((s) => !STATUS_ORDER.includes(s) && s !== '')
    .sort((a, b) => byStatus.get(b)!.length - byStatus.get(a)!.length || a.localeCompare(b));

  const bands: Band[] = [];
  if (onRegister.length) {
    bands.push({
      key: REGISTER_BAND,
      label: 'On the Register of Native Title Claims',
      plain: REGISTER_PLAIN,
      rows: onRegister,
    });
  }
  for (const s of [...known, ...extra]) {
    bands.push({
      key: s,
      label: s,
      plain: STATUS_PLAIN[s] ?? 'The register states this status for these matters, in these words.',
      rows: byStatus.get(s)!,
    });
  }
  const blank = byStatus.get('');
  if (blank) {
    bands.push({
      key: 'unrecorded',
      label: 'Registration status not recorded',
      plain: 'The register does not state a registration status for these matters. That is its own third state — it is not the same as “not accepted”, and it is not treated as one.',
      rows: blank,
    });
  }
  for (const b of bands) {
    b.rows.sort((a, c) => (a.lodged ?? '').localeCompare(c.lodged ?? '') || a.id.localeCompare(c.id));
  }
  return bands;
}

/* ───────────────────────── application type wording ────────────────────── */

const TYPE_ORDER = ['Claimant', 'Non-Claimant', 'Compensation', 'Revised Native Title Determination'];

const TYPE_PLAIN: Record<string, string> = {
  Claimant: 'An application by a group asking the Federal Court to determine that native title exists over an area, who holds it, and what it allows them to do.',
  'Non-Claimant': 'An application by someone who does not claim native title — usually a government, a council or a landholder — asking the Court to determine whether native title exists over an area.',
  Compensation: 'An application for compensation because a government act extinguished or impaired native title.',
  'Revised Native Title Determination': 'An application to revise or revoke a determination the Court has already made.',
};

/* ───────────────────────────── layout constants ────────────────────────── */

const ROW_PITCH = 5;
const RULE_W = 3;
const HEADER_H = 24;
const HEADER_PAD = 4;
const BAND_GAP = 16;
const AXIS_TOP = 30;
const AXIS_BOTTOM = 26;
const PAD_L = 10;
const PAD_R = 16;
const MIN_WIDTH = 560;
/** A rough advance width for the 11.5px band label, used only to decide whether a right-hand count fits. */
const CHAR_W = 6.1;

/**
 * The width to render at.
 *
 * The host div a view receives is DETACHED — main.ts moves its children into the
 * page afterwards — so measuring the host itself always yields 0 and silently
 * falls back to a default. The live `.view-wrap` is in the document and is the
 * box this chart will actually sit in.
 */
function containerWidth(fallback = 900): number {
  const wrap = document.querySelector<HTMLElement>('#main-content .view-wrap')
    ?? document.querySelector<HTMLElement>('.view-wrap');
  if (!wrap) return fallback;
  const cs = getComputedStyle(wrap);
  const w = wrap.clientWidth - parseFloat(cs.paddingLeft || '0') - parseFloat(cs.paddingRight || '0');
  return w > 40 ? Math.floor(w) : fallback;
}

/* ──────────────────────────────── the view ─────────────────────────────── */

export async function renderPending(host: HTMLElement): Promise<void> {
  const meta = getMeta();
  const [rawMatters, jurisdictions] = await Promise.all([loadMatters(), loadJurisdictions()]);

  const matters = mergeMatters(rawMatters);
  const rawRows = rawMatters.length;
  const today = meta.builtAt.slice(0, 10);
  const todayMs = msOf(today) ?? Date.now();
  const jurisName = new Map<string, string>(jurisdictions.map((j) => [j.code, j.name]));

  const allBands = buildBands(matters);

  // ── the summary numbers, every one of them measured from the merged cohort ──
  const ages = matters.map((m) => m.daysSinceLodged).filter((d): d is number => d != null).sort((a, b) => a - b);
  const median = ages.length ? ages[Math.floor(ages.length / 2)] : null;
  const oldest = matters.reduce<Matter | null>(
    (a, b) => (a == null || (b.daysSinceLodged ?? -1) > (a.daysSinceLodged ?? -1) ? b : a), null);
  const overTen = matters.filter((m) => (m.daysSinceLodged ?? 0) > 3653).length;
  const overTwenty = matters.filter((m) => (m.daysSinceLodged ?? 0) > 7305).length;
  const onRegisterCount = matters.filter((m) => m.onRegister).length;
  const reTested = matters.filter((m) => /new decision in progress/i.test(m.registrationStatus ?? '')).length;

  const typeCounts = new Map<string, number>();
  for (const m of matters) {
    const k = m.applicationType ?? 'Not recorded';
    typeCounts.set(k, (typeCounts.get(k) ?? 0) + 1);
  }
  const typeKeys = [
    ...TYPE_ORDER.filter((t) => typeCounts.has(t)),
    ...[...typeCounts.keys()].filter((t) => !TYPE_ORDER.includes(t)),
  ];

  const jurisCounts = new Map<string, number>();
  for (const m of matters) jurisCounts.set(m.juris, (jurisCounts.get(m.juris) ?? 0) + 1);

  // ── the calendar domain ──
  const allMs: number[] = [todayMs];
  for (const m of matters) {
    for (const a of ANCHORS) {
      const t = msOf(m[a.key]);
      if (t != null) allMs.push(t);
    }
  }
  const startYear = new Date(Math.min(...allMs)).getUTCFullYear();
  const t0 = Date.UTC(startYear, 0, 1);
  const t1 = Math.max(Math.max(...allMs), todayMs);
  const endYear = new Date(t1).getUTCFullYear();

  /* ───────────────────────────── filter state ──────────────────────────── */

  const filters: { band: string | null; juris: string | null; type: string | null } = {
    band: null, juris: null, type: null,
  };

  function visibleBands(): Band[] {
    return allBands
      .filter((b) => filters.band == null || b.key === filters.band)
      .map((b) => ({
        ...b,
        rows: b.rows.filter((m) => (filters.juris == null || m.juris === filters.juris)
          && (filters.type == null || (m.applicationType ?? 'Not recorded') === filters.type)),
      }))
      .filter((b) => b.rows.length > 0);
  }

  /* ────────────────────────────── page markup ──────────────────────────── */

  const wrap = document.createElement('div');
  wrap.className = 'stack';

  const headEl = document.createElement('div');
  headEl.className = 'view-head';
  headEl.innerHTML = `
    <h1>Still open</h1>
    <p class="lede">${esc(num(matters.length))} native title matters are live before the Federal Court and the
      National Native Title Tribunal today. This page places each of them on a real calendar: which statutory
      stage it has reached, and how long it has been sitting there.</p>
    <p class="small">Half have been open longer than ${esc(duration(median))}, and the longest has been open
      ${esc(duration(oldest?.daysSinceLodged ?? null))}. ${esc(num(onRegisterCount))} of the
      ${esc(num(matters.length))} have met the ${glossTerm('registration-test', 'registration test')} and sit on the
      Register of Native Title Claims; the other ${esc(num(matters.length - onRegisterCount))} sit on the
      ${glossTerm('schedule-of-applications', 'Schedule of Applications')} only. None of them has been decided.</p>`;

  /* ── summary stats ── */
  const stats = document.createElement('div');
  stats.className = 'stat-row';

  const statLive = document.createElement('div');
  statLive.className = 'stat';
  statLive.setAttribute('data-tip', tip(
    `${plural(matters.length, 'live matter')} — every application still before the Court or the Tribunal.`,
    `${plural(onRegisterCount, 'claim')} of them are also entered on the Register of Native Title Claims.`,
    `The source file holds ${num(rawRows)} rows because those ${num(onRegisterCount)} claims are listed in both registers. Each matter is counted once here.`,
  ));
  statLive.innerHTML = `<div class="stat-value">${esc(num(matters.length))}</div>
    <div class="stat-label">Live matters</div>
    <div class="stat-note">${esc(num(onRegisterCount))} on the Register of Native Title Claims,
      ${esc(num(matters.length - onRegisterCount))} on the Schedule only</div>`;

  const statMedian = document.createElement('div');
  statMedian.className = 'stat';
  statMedian.setAttribute('data-tip', tip(
    `Median time since lodgement: ${median == null ? 'not available' : span(median)}.`,
    'Half of the live matters have been open longer than this and half less.',
    'This is AGE, not time to an outcome — determined matters have left these registers entirely.',
  ));
  statMedian.innerHTML = `<div class="stat-value">${esc(duration(median))}</div>
    <div class="stat-label">Median time since lodgement</div>
    <div class="stat-note">${median == null ? 'Not available' : esc(span(median))} — age, not time to an outcome</div>`;

  const statOld = document.createElement('div');
  statOld.className = 'stat';
  statOld.setAttribute('data-tip', tip(
    `${plural(overTen, 'matter')} have been open for more than ten years.`,
    `${plural(overTwenty, 'matter')} for more than twenty.`,
    'Age is not evidence of anything about a matter or about the people who brought it. Connection research, statutory notification and negotiation with every party who holds an interest in the area all take years.',
  ));
  statOld.innerHTML = `<div class="stat-value">${esc(num(overTen))}</div>
    <div class="stat-label">Open more than ten years</div>
    <div class="stat-note">${esc(num(overTwenty))} of them more than twenty years</div>`;

  stats.append(statLive, statMedian, statOld);

  // Only added when there IS an oldest matter to open — a stat tile styled as a
  // button with nothing behind it would be a dead click.
  if (oldest) {
    const statOldest = document.createElement('button');
    statOldest.type = 'button';
    statOldest.className = 'stat pending-stat-btn';
    statOldest.setAttribute('aria-label',
      `Read the record for ${oldest.name ?? oldest.id}, the matter that has been open longest — ${duration(oldest.daysSinceLodged)} since it was lodged on ${longDate(oldest.lodged)}`);
    statOldest.setAttribute('data-tip', tip(
      `${oldest.name ?? oldest.id} (${oldest.id})`,
      `Lodged ${longDate(oldest.lodged)} in ${jurisName.get(oldest.juris) ?? oldest.juris}.`,
      `Open ${span(oldest.daysSinceLodged ?? 0)} and still live.`,
      'Click to read the full record.',
    ));
    // The register's own name for the matter is `.verbatim`, which is
    // `white-space: pre-wrap` — so the markup around it must carry NO source
    // newlines or the reader sees the template's indentation rendered as a line
    // break and eight literal spaces.
    statOldest.innerHTML = `<div class="stat-value">${esc(duration(oldest.daysSinceLodged))}</div>
      <div class="stat-label">Longest time since lodgement</div>
      <div class="stat-note"><span class="verbatim">${esc(oldest.name ?? oldest.id)}</span> · ${esc(oldest.id)} · lodged ${esc(longDate(oldest.lodged))}</div>`;
    statOldest.addEventListener('click', () => openRecord(oldest));
    stats.insertBefore(statOldest, statOld);
  }

  /* ── the two mandatory, always-visible notes ── */
  const noteSurvivorship = document.createElement('div');
  noteSurvivorship.className = 'note caveat';
  noteSurvivorship.innerHTML = `<strong>This is age, not duration to an outcome.</strong>
    These two registers hold only matters that are still live today. A matter that has been determined has left
    them, and the National Native Title Register — the record of the ${esc(num(meta.counts.determinations))}
    ${glossTerm('determination', 'determinations')} made since 1992 — carries no lodgement date at all. A true
    lodged-to-determined duration therefore <em>cannot</em> be computed from these files, and this site does not
    invent one. Every elapsed time on this page is how long a matter has been open so far. These
    ${esc(num(matters.length))} live matters are also <em>not</em> the cohort that produced those
    ${esc(num(meta.counts.determinations))} determinations, so nothing here can be read as a rate of anything.`;

  const noteThreshold = document.createElement('div');
  noteThreshold.className = 'note warn caveat';
  noteThreshold.innerHTML = `<strong>The registration test is a threshold, not a verdict.</strong>
    “Not accepted for registration” means the application did not meet the
    ${glossTerm('registration-test', 'section 190A test')} at the time it was tested. It can be amended and tested
    again — ${esc(plural(reTested, 'matter'))} in this strip ${reTested === 1 ? 'is' : 'are'} under a fresh
    registration decision right now — and it says nothing about the merits of the application, nothing about
    whether ${glossTerm('native-title', 'native title')} exists over the area, and nothing about the people who
    brought it. Several application types are never registration-tested at all: non-claimant,
    ${glossTerm('compensation', 'compensation')} and revised determination applications sit in the
    “Not currently identified for Reg. Decision” band for that reason alone.`;

  /* ── filters ── */
  const controls = document.createElement('div');
  controls.className = 'stack-sm pending-filters';

  const bandRow = document.createElement('div');
  bandRow.className = 'chips';
  const typeRow = document.createElement('div');
  typeRow.className = 'chips';
  const jurisRow = document.createElement('div');
  jurisRow.className = 'chips';
  const clearRow = document.createElement('div');
  clearRow.className = 'chips';

  const chipButtons: { btn: HTMLButtonElement; active: () => boolean }[] = [];

  function chipLabel(text: string): HTMLSpanElement {
    const s = document.createElement('span');
    s.className = 'small muted shrink0';
    s.textContent = text;
    return s;
  }

  function makeChip(opts: {
    label: string; count: number; tipText: string; active: () => boolean; toggle: () => void;
  }): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-sm pending-chip';
    b.setAttribute('data-tip', opts.tipText);
    b.innerHTML = `<span class="pending-chip-name">${esc(opts.label)}</span>
      <span class="mono xsmall pending-chip-n">${esc(num(opts.count))}</span>`;
    b.addEventListener('click', () => { opts.toggle(); syncChips(); redraw(); });
    chipButtons.push({ btn: b, active: opts.active });
    return b;
  }

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'btn btn-sm';
  clearBtn.textContent = 'Show every live matter';
  clearBtn.setAttribute('data-tip', 'Clear every filter and put all live matters back on the strip.');
  clearBtn.addEventListener('click', () => {
    filters.band = null; filters.juris = null; filters.type = null;
    syncChips(); redraw();
  });
  clearRow.append(clearBtn);

  function syncChips(): void {
    for (const c of chipButtons) c.btn.setAttribute('aria-pressed', String(c.active()));
    clearBtn.hidden = filters.band == null && filters.juris == null && filters.type == null;
  }

  bandRow.append(chipLabel('Stage:'));
  for (const b of allBands) {
    bandRow.append(makeChip({
      label: b.label,
      count: b.rows.length,
      tipText: tip(
        b.label,
        `${plural(b.rows.length, 'live matter')} in total — ${pct((b.rows.length / matters.length) * 100)} of all live matters.`,
        b.plain,
        'Click to keep only this band on the strip.',
      ),
      active: () => filters.band === b.key,
      toggle: () => { filters.band = filters.band === b.key ? null : b.key; },
    }));
  }

  typeRow.append(chipLabel('Application type:'));
  for (const t of typeKeys) {
    const n = typeCounts.get(t) ?? 0;
    typeRow.append(makeChip({
      label: t,
      count: n,
      tipText: tip(
        t,
        `${plural(n, 'live matter')} in total — ${pct((n / matters.length) * 100)} of all live matters.`,
        TYPE_PLAIN[t] ?? 'The register states this application type in these words.',
        'Click to keep only this application type on the strip.',
      ),
      active: () => filters.type === t,
      toggle: () => { filters.type = filters.type === t ? null : t; },
    }));
  }

  jurisRow.append(chipLabel('Jurisdiction:'));
  for (const j of jurisdictions) {
    const n = jurisCounts.get(j.code) ?? 0;
    jurisRow.append(makeChip({
      label: j.name,
      count: n,
      tipText: n === 0
        ? tip(
          j.name,
          '0 live matters — a real, measured zero, not missing data.',
          j.determinations === 0
            ? `No native title application is before the Federal Court in ${j.name}, and no determination has ever been made there.`
            : `No native title application is before the Federal Court in ${j.name}. ${plural(j.determinations, 'determination')} have been made there.`,
          'Click to see that zero stated in full.',
        )
        : tip(
          j.name,
          `${plural(n, 'live matter')} in total — ${pct((n / matters.length) * 100)} of all live matters.`,
          `${plural(j.determinations, 'determination')} have been made in ${j.name}.`,
          'Click to keep only this jurisdiction on the strip.',
        ),
      active: () => filters.juris === j.code,
      toggle: () => { filters.juris = filters.juris === j.code ? null : j.code; },
    }));
  }

  controls.append(bandRow, typeRow, jurisRow, clearRow);

  /* ── the strip ── */
  const caption = document.createElement('p');
  caption.className = 'small muted';
  caption.innerHTML = `Each rule is one live matter, laid on the calendar from the year it was lodged through to
    today. The rule is segmented at every date the register records — lodged, entered on the Register,
    notification closed, most recent Federal Court order — and then runs on as an open arrow, because the matter
    has not finished. Where a date is not recorded, the stretch it would have divided is hatched instead: never
    closed up to nothing, never treated as a zero. Hover any segment for its exact dates; click any rule for the
    full record. Scroll to zoom, drag to pan, double-click to reset.`;

  const frame = document.createElement('div');
  frame.className = 'chart-frame pending-frame';
  const scroller = document.createElement('div');
  scroller.className = 'chart-scroll';
  frame.append(scroller);

  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.innerHTML = [
    ...STAGES.map((s, i) => `<span class="legend-item" data-tip="${tipAttr(s.title, s.act)}">
      <span class="legend-swatch" style="background:${STAGE_INK[i]}"></span>
      <span>${esc(s.title)}</span></span>`),
    `<span class="legend-item" data-tip="${tipAttr('Still open', 'The matter has not been determined, so the rule has no end. The arrowhead sits at today.')}">
      <svg width="30" height="12" aria-hidden="true" class="pending-legend-svg">
        <line x1="1" y1="6" x2="21" y2="6" stroke="${PENDING_STROKE}" stroke-width="3"></line>
        <path d="M21,2 L29,6 L21,10 Z" fill="${PENDING_STROKE}"></path></svg>
      <span>Still open — no end date</span></span>`,
    `<span class="legend-item" data-tip="${tipAttr('Date not recorded', 'The register does not record the date that would divide this stretch. It is its own third state — never a zero, never closed up, never folded into “no”.')}">
      <span class="legend-swatch" style="background:repeating-linear-gradient(45deg, ${UNRECORDED} 0 2px, #efeae0 2px 6px)"></span>
      <span>Date not recorded</span></span>`,
    `<span class="legend-item" data-tip="${tipAttr('A recorded date that does not run in sequence', 'The register sometimes states a registration date that falls after the notification date — usually because the application was amended and tested again. Those dates are marked as ticks where they actually fall, rather than forced into an order they do not have.')}">
      <svg width="14" height="12" aria-hidden="true" class="pending-legend-svg">
        <line x1="7" y1="1" x2="7" y2="11" stroke="#47433b" stroke-width="1.6"></line></svg>
      <span>Date out of sequence</span></span>`,
  ].join('');

  const extracted = meta.sources.map((s) => s.extracted).filter(Boolean).sort();
  const stripNote = document.createElement('p');
  stripNote.className = 'xsmall muted';
  stripNote.textContent = `Bands are the register's own words for the stage a matter has reached. Band height is `
    + `proportional to how many matters sit in it; inside a band, matters run by lodgement date with the earliest `
    + `at the top. Areas quoted anywhere on this page are indicative only and are not legal boundaries. Registers `
    + `extracted ${longDate(extracted[extracted.length - 1] ?? null)}; "today" on this axis is ${longDate(today)}.`;

  const a11ySummary = document.createElement('p');
  a11ySummary.className = 'visually-hidden';

  wrap.append(headEl, stats, noteSurvivorship, noteThreshold, controls, caption, frame, legend, stripNote, a11ySummary);
  host.append(wrap);

  /* ───────────────────────────── the render pass ───────────────────────── */

  let zoom: SvgZoomHandle | null = null;
  let lastWidth = 0;

  function redraw(): void {
    zoom?.destroy();
    zoom = null;
    scroller.replaceChildren();

    const bands = visibleBands();
    const shown = bands.reduce((n, b) => n + b.rows.length, 0);

    a11ySummary.textContent = `A calendar strip of ${shown} live native title matters, one row each, running from `
      + `${startYear} to ${longDate(today)} and grouped into ${bands.length} bands by the stage each matter has `
      + `reached. Every matter's full date chain is written out in the record card that opens from its row.`;

    if (!bands.length) {
      scroller.append(emptyState());
      return;
    }

    const width = Math.max(MIN_WIDTH, containerWidth());
    lastWidth = width;
    const x0 = PAD_L;
    const x1 = width - PAD_R;
    const plotW = x1 - x0;
    const scale = (ms: number) => x0 + ((ms - t0) / (t1 - t0)) * plotW;

    let height = AXIS_TOP;
    for (const b of bands) height += BAND_GAP + HEADER_H + HEADER_PAD + b.rows.length * ROW_PITCH;
    height += AXIS_BOTTOM;

    const svg = chart(width, height, `Live native title matters by statutory stage, ${startYear} to ${longDate(today)}`);
    svg.setAttribute('class', 'chart pending-strip');
    defs(svg);

    const gridTop = AXIS_TOP - 8;
    const gridBottom = height - AXIS_BOTTOM + 4;
    const todayX = Math.round(scale(todayMs) * 10) / 10;

    // ── the calendar grid ──
    const spanYears = Math.max(endYear - startYear + 1, 1);
    const rawStep = Math.ceil(46 / Math.max(plotW / spanYears, 0.01));
    const step = [1, 2, 5, 10, 20].find((s) => s >= rawStep) ?? 25;
    const grid = el('g');
    for (let yr = Math.ceil(startYear / step) * step; yr <= endYear; yr += step) {
      const gx = Math.round(scale(Date.UTC(yr, 0, 1)) * 10) / 10;
      if (gx < x0 - 1 || gx > x1 + 1) continue;
      grid.append(el('line', { x1: gx, y1: gridTop, x2: gx, y2: gridBottom, class: 'grid-line' }));
      grid.append(el('text', { x: gx, y: 14, class: 'axis-text', 'text-anchor': 'middle' }, String(yr)));
      // The bottom label is suppressed next to the "today" marker so the two
      // never overprint each other.
      if (Math.abs(gx - todayX) > 36) {
        grid.append(el('text', { x: gx, y: height - 8, class: 'axis-text', 'text-anchor': 'middle' }, String(yr)));
      }
    }
    svg.append(grid);

    // ── the "today" rule ──
    const todayG = el('g');
    todayG.setAttribute('data-tip', tip(
      `Today — ${longDate(today)}`,
      'Every rule reaching this line is a matter that has not finished. Nothing on this page measures time to an outcome.',
      'This is the date the registers were last read for this site.',
    ));
    todayG.append(el('line', { x1: todayX, y1: gridTop, x2: todayX, y2: gridBottom, class: 'pending-today-line' }));
    todayG.append(el('text', {
      x: Math.min(todayX - 4, x1), y: height - 8, class: 'pending-today-label', 'text-anchor': 'end',
    }, 'today'));
    svg.append(todayG);

    // ── the bands ──
    let y = AXIS_TOP;
    for (const band of bands) {
      y += BAND_GAP;
      svg.append(bandHeader(band, y, x0, x1, plotW));
      y += HEADER_H + HEADER_PAD;
      for (const m of band.rows) {
        svg.append(drawRow(m, y, scale, x0, x1));
        y += ROW_PITCH;
      }
    }

    scroller.append(svg);
    zoom = attachSvgZoom(svg, { maxScale: 14 });
  }

  /* ────────────────────────────── band header ──────────────────────────── */

  function bandHeader(band: Band, y: number, x0: number, x1: number, plotW: number): SVGGElement {
    const g = el('g', { class: 'hit' });
    g.setAttribute('data-tip', tip(
      band.label,
      `${plural(band.rows.length, 'live matter')} in this band right now.`,
      band.plain,
      filters.band === band.key ? 'Click to put every band back on the strip.' : 'Click to keep only this band.',
    ));
    // The chip row above is the keyboard-accessible control for exactly this
    // action. The in-chart duplicate stays out of the accessibility tree rather
    // than adding a second, silent tab stop for the same thing.
    g.setAttribute('aria-hidden', 'true');
    g.append(el('rect', { x: x0, y, width: plotW, height: HEADER_H, rx: 3, class: 'pending-band-head' }));
    g.append(el('rect', { x: x0, y, width: 3, height: HEADER_H, class: 'pending-band-key' }));
    g.append(el('text', { x: x0 + 10, y: y + 16, class: 'pending-band-label' }, band.label));
    const countText = `${num(band.rows.length)} ${band.rows.length === 1 ? 'matter' : 'matters'}`;
    if ((band.label.length + countText.length) * CHAR_W + 44 < plotW) {
      g.append(el('text', { x: x1 - 10, y: y + 16, class: 'pending-band-count', 'text-anchor': 'end' }, countText));
    }
    g.addEventListener('click', () => {
      filters.band = filters.band === band.key ? null : band.key;
      syncChips();
      redraw();
    });
    return g;
  }

  /* ──────────────────────────────── one row ────────────────────────────── */

  function drawRow(m: Matter, top: number, scale: (ms: number) => number, x0: number, x1: number): SVGGElement {
    const g = el('g', { class: 'pending-row hit' });
    const cy = top + ROW_PITCH / 2;
    const chain = chainOf(m);

    g.setAttribute('data-tip', rowTip(m, chain));
    g.addEventListener('click', () => openRecord(m));

    // Drawn first so it sits UNDER the marks: a hit area painted on top would
    // swallow every segment tooltip on the row.
    g.append(el('rect', { x: x0, y: top, width: x1 - x0, height: ROW_PITCH, class: 'pending-rowbg' }));

    const put = (a: number, b: number) => {
      const xa = Math.round(scale(Math.min(a, b)) * 10) / 10;
      const xb = Math.round(Math.max(scale(Math.max(a, b)), xa + 1.4) * 10) / 10;
      return [xa, xb] as const;
    };

    for (const seg of chain.segments) {
      const [xa, xb] = put(seg.a, seg.b);
      if (seg.gap > 1) {
        const r = el('rect', {
          x: xa, y: cy - RULE_W / 2, width: xb - xa, height: RULE_W, fill: UNRECORDED_FILL, class: 'mark',
        });
        r.setAttribute('data-tip', tip(
          'The register does not record this step',
          `Not recorded: ${seg.missing.join(', ')}.`,
          `This stretch runs ${longDate(anchorIso(m, seg.from))} → ${longDate(anchorIso(m, seg.to))}, ${span(dayCount(seg.a, seg.b))}.`,
          `It covers ${seg.stages.join(' and ')}, and cannot be split between them.`,
          'It is hatched as its own state — never closed up, never a zero.',
        ));
        g.append(r);
      } else {
        const stage = STAGES[seg.from];
        const line = el('line', {
          x1: xa, y1: cy, x2: xb, y2: cy, stroke: STAGE_INK[seg.from], 'stroke-width': RULE_W, class: 'mark',
        });
        line.setAttribute('data-tip', tip(
          stage.title,
          stage.act,
          `${ANCHORS[seg.from].label}: ${longDate(anchorIso(m, seg.from))}`,
          `${ANCHORS[seg.to].label}: ${longDate(anchorIso(m, seg.to))}`,
          `Elapsed: ${span(dayCount(seg.a, seg.b))}.`,
        ));
        g.append(line);
      }
    }

    // Recorded dates the chain could not use, because the register puts them out
    // of sequence. They are marked where they actually fall, not reordered.
    for (const t of chain.strayTicks) {
      const tx = Math.round(scale(t.ms) * 10) / 10;
      const tick = el('line', { x1: tx, y1: top, x2: tx, y2: top + ROW_PITCH, class: 'pending-tick mark' });
      tick.setAttribute('data-tip', tip(
        `${t.label}: ${longDate(t.iso)}`,
        'The register states this date out of sequence with the others for this matter — usually because the application was amended and tested again.',
        'It is marked where it falls rather than forced into an order it does not have.',
      ));
      g.append(tick);
    }

    // The open end. Never a cap and never a closing tick: the matter has not
    // ended, and a terminated line would say that it had.
    const arrowEnd = Math.max(todayMs, chain.lastMs);
    const arrowStart = Math.min(chain.lastMs, todayMs);
    const ax = Math.round(scale(arrowStart) * 10) / 10;
    const bx = Math.round(scale(arrowEnd) * 10) / 10;
    const age = m.daysSinceLodged ?? dayCount(chain.lodgedMs, todayMs);
    const arrow = el('g', { class: 'mark' });
    arrow.setAttribute('data-tip', tip(
      `${m.name ?? m.id} — still live`,
      `Lodged ${plural(age, 'day')} ago, on ${longDate(m.lodged)}, and still live.`,
      `That is ${duration(age)} and counting. No determination has been made, so this line has no end.`,
      chain.lastMs > todayMs ? `The most recent recorded date, ${longDate(chain.lastIso)}, is still ahead of today.` : null,
    ));
    if (bx > ax + 0.5) {
      arrow.append(el('line', { x1: ax, y1: cy, x2: bx, y2: cy, stroke: PENDING_STROKE, 'stroke-width': RULE_W }));
    }
    arrow.append(el('path', {
      d: `M${bx},${cy - 2.6} L${bx + 5.4},${cy} L${bx},${cy + 2.6} Z`, fill: PENDING_STROKE,
    }));
    g.append(arrow);

    return g;
  }

  /* ─────────────────────────── the chain, per matter ───────────────────── */

  interface Segment { from: number; to: number; a: number; b: number; gap: number; missing: string[]; stages: string[] }
  interface Chain {
    segments: Segment[];
    strayTicks: { ms: number; iso: string; label: string }[];
    lastMs: number;
    lastIso: string;
    lodgedMs: number;
    outOfSequence: { earlier: string; earlierIso: string; later: string; laterIso: string } | null;
  }

  function anchorIso(m: Matter, i: number): string | null {
    return m[ANCHORS[i].key];
  }

  function chainOf(m: Matter): Chain {
    const known = ANCHORS.map((a) => msOf(m[a.key]));
    const lodgedMs = known[0] ?? todayMs;
    const segments: Segment[] = [];
    const strayTicks: { ms: number; iso: string; label: string }[] = [];
    let outOfSequence: Chain['outOfSequence'] = null;

    let prev = known[0] == null ? -1 : 0;
    let stopped = prev < 0;

    for (let j = 1; j < ANCHORS.length; j++) {
      const here = known[j];
      if (here == null) continue;
      if (stopped) {
        strayTicks.push({ ms: here, iso: m[ANCHORS[j].key] ?? '', label: ANCHORS[j].label });
        continue;
      }
      const a = known[prev]!;
      if (here < a) {
        // The register puts this date before one that the Act puts after it.
        // Segmenting past this point would invent an order the data does not
        // have, so the chain stops and the rest is marked as ticks.
        outOfSequence = {
          earlier: ANCHORS[j].label,
          earlierIso: m[ANCHORS[j].key] ?? '',
          later: ANCHORS[prev].label,
          laterIso: m[ANCHORS[prev].key] ?? '',
        };
        stopped = true;
        strayTicks.push({ ms: here, iso: m[ANCHORS[j].key] ?? '', label: ANCHORS[j].label });
        continue;
      }
      const missing: string[] = [];
      const stages: string[] = [];
      for (let k = prev + 1; k < j; k++) missing.push(ANCHORS[k].label.toLowerCase());
      for (let k = prev; k < j; k++) stages.push(STAGES[k].title.toLowerCase());
      segments.push({ from: prev, to: j, a, b: here, gap: j - prev, missing, stages });
      prev = j;
    }

    const lastIdx = prev < 0 ? 0 : prev;
    return {
      segments,
      strayTicks,
      lastMs: known[lastIdx] ?? lodgedMs,
      lastIso: m[ANCHORS[lastIdx].key] ?? m.lodged ?? '',
      lodgedMs,
      outOfSequence,
    };
  }

  function rowTip(m: Matter, chain: Chain): string {
    return tip(
      m.name ?? m.id,
      `${m.id} · ${jurisName.get(m.juris) ?? m.juris} · ${m.applicationType ?? 'application type not recorded'}`,
      '',
      `Lodged: ${longDate(m.lodged)} — ${span(m.daysSinceLodged ?? 0)} ago`,
      `Entered on the Register of Native Title Claims: ${m.registeredOn ? longDate(m.registeredOn) : 'not recorded'}`,
      `Notification closed: ${m.notificationClosed ? longDate(m.notificationClosed) : 'not recorded'}`,
      `Most recent Federal Court order: ${m.courtOrder ? longDate(m.courtOrder) : 'not recorded'}`,
      '',
      `Status: ${m.status ?? 'not recorded'} · ${m.onRegister ? 'on the Register of Native Title Claims' : 'on the Schedule of Applications only'}`,
      `Registration status: ${m.registrationStatus ?? 'not recorded'}`,
      `Representative: ${m.representative ?? 'not recorded'}`,
      `Area applied for: ${km2(m.areaSqKm)}, indicative${m.sea ? ' · includes sea country' : ''}`,
      chain.outOfSequence ? 'The register states these dates out of sequence — the record card explains.' : null,
      '',
      'Click for the full record.',
    );
  }

  /* ───────────────────────────── the empty state ───────────────────────── */

  function emptyState(): HTMLElement {
    const box = document.createElement('div');
    box.className = 'card pending-zero';
    const j = filters.juris ? jurisdictions.find((x) => x.code === filters.juris) : null;
    const parts: string[] = [];

    if (j && filters.band == null && filters.type == null) {
      parts.push(`<h2 class="card-title">No live matters in ${esc(j.name)} — a real zero</h2>
        <p class="small">0 native title applications are before the Federal Court in ${esc(j.name)} today. That is
        a measured zero, not missing data: this jurisdiction is present in every register on this site and its
        count is nought.</p>
        <p class="small">${j.determinations === 0
          ? `No ${glossTerm('determination', 'determination')} has ever been made in ${esc(j.name)} either.`
          : `${esc(plural(j.determinations, 'determination'))} have been made in ${esc(j.name)}.`}</p>`);
      if (j.code === 'TAS') {
        parts.push(`<p class="small muted">Tasmania has no determinations, no live matters, and no recognised
          ${glossTerm('ratsib', 'representative body')} under the Native Title Act. The zero is the finding, and
          it is stated rather than left blank.</p>`);
      }
      if (j.code === 'ACT') {
        parts.push(`<p class="small muted">The Australian Capital Territory has no determinations and no live
          matters on these registers.</p>`);
      }
    } else if (j) {
      parts.push(`<h2 class="card-title">No live matter in ${esc(j.name)} matches the rest of these filters</h2>
        <p class="small">${esc(plural(jurisCounts.get(j.code) ?? 0, 'live matter'))} sit in ${esc(j.name)} in
        total, but nought of them are in every filter you have chosen at once. That is a real zero for this
        combination, not missing data.</p>`);
    } else {
      parts.push(`<h2 class="card-title">No live matter matches this combination</h2>
        <p class="small">0 of the ${esc(num(matters.length))} live matters sit in every one of the filters you
        have chosen at once. That is a real zero for this combination, not missing data.</p>`);
    }

    box.innerHTML = parts.join('');
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-sm';
    b.textContent = 'Show every live matter';
    b.addEventListener('click', () => {
      filters.band = null; filters.juris = null; filters.type = null;
      syncChips(); redraw();
    });
    box.append(b);
    return box;
  }

  /* ───────────────────────────── the record card ───────────────────────── */

  function openRecord(m: Matter): void {
    const chain = chainOf(m);
    const jn = jurisName.get(m.juris) ?? m.juris;
    const type = m.applicationType ?? null;

    openOverlay({
      panelClass: 'drawer',
      label: `Live matter: ${m.name ?? m.id}`,
      lockScroll: true,
      build(panel, close) {
        const cardHead = document.createElement('div');
        cardHead.className = 'overlay-head';
        const h = document.createElement('h2');
        h.className = 'verbatim';
        h.textContent = m.name ?? m.id;
        cardHead.append(h, closeButton(close, 'Close this record'));

        const chainRows = ANCHORS.map((a, i) => {
          const iso = m[a.key];
          const prevIso = i > 0 ? m[ANCHORS[i - 1].key] : null;
          const gap = iso && prevIso ? dayCount(msOf(prevIso)!, msOf(iso)!) : null;
          const ahead = iso ? (msOf(iso) ?? 0) > todayMs : false;
          const notes = [
            gap == null ? '' : (gap < 0
              ? `${span(Math.abs(gap))} <em>before</em> the previous date — the register states them out of sequence`
              : `${span(gap)} after the previous recorded date`),
            ahead ? 'This date is still ahead of today.' : '',
          ].filter(Boolean).join(' · ');
          return `<div class="pending-chain-row">
            <div class="detail-label">${esc(a.label)}</div>
            <div class="${iso ? 'mono' : 'pending-unrecorded'}">${iso ? esc(longDate(iso)) : 'Not recorded'}</div>
            <div class="xsmall muted">${iso ? notes
              : 'The register leaves this blank. It is not a zero and it is not a “no”.'}</div>
          </div>`;
        }).join('');

        const body = document.createElement('div');
        body.className = 'stack';
        body.innerHTML = `
          <div class="detail-headline">
            <span class="pill">${esc(type ?? 'Application type not recorded')}</span>
            <span class="pill">${esc(jn)}</span>
            <span class="pill" style="border-color:${PENDING_STROKE}">${m.onRegister
              ? 'On the Register of Native Title Claims'
              : 'On the Schedule of Applications only'}</span>
            ${m.sea ? '<span class="pill">Includes sea country</span>' : ''}
          </div>
          <p class="small muted">${esc(type
            ? (TYPE_PLAIN[type] ?? 'The register states this application type in these words.')
            : 'The register does not record what kind of application this is.')}</p>

          <div class="detail-block">
            <h3>How long it has been open${glossIcon('schedule-of-applications')}</h3>
            <p><strong class="mono">${esc(span(m.daysSinceLodged ?? 0))}</strong> since it was lodged on
              ${esc(longDate(m.lodged))}, and it is still live.</p>
            <p class="xsmall muted">That is age, not time to an outcome. A matter that has been determined leaves
              these registers, and the determination register records no lodgement date — so no lodged-to-determined
              duration can be computed from this data, and none appears anywhere on this site.</p>
          </div>

          <div class="detail-block">
            <h3>The date chain</h3>
            <p class="xsmall muted">Four dates, in the order the Native Title Act puts them. Where the register does
              not record one, it is written out as “Not recorded” — never as a zero and never quietly skipped.</p>
            <div class="pending-chain">${chainRows}</div>
          </div>

          ${chain.outOfSequence ? `<div class="note warn small">The register states these dates out of sequence:
            ${esc(chain.outOfSequence.later.toLowerCase())} is given as
            ${esc(longDate(chain.outOfSequence.laterIso))}, which falls after
            ${esc(chain.outOfSequence.earlier.toLowerCase())} on
            ${esc(longDate(chain.outOfSequence.earlierIso))}. That usually means the application was amended and
            tested again. The strip therefore segments this matter only as far as its dates run in order, and marks
            the remaining recorded dates as ticks where they actually fall.</div>` : ''}

          <div class="detail-block">
            <h3>Where it stands${glossIcon('registration-test')}</h3>
            <div class="detail-grid">
              <div class="detail-field">
                <div class="detail-label">Registration status, in the register’s words</div>
                <div class="verbatim">${esc(m.registrationStatus ?? 'Not recorded')}</div>
              </div>
              <div class="detail-field">
                <div class="detail-label">Status</div>
                <div>${esc(m.status ?? 'Not recorded')}</div>
              </div>
            </div>
            <p class="small muted">${esc(m.registrationStatus
              ? (STATUS_PLAIN[m.registrationStatus] ?? 'The register states this status in these words.')
              : 'The register does not state a registration status for this matter.')}</p>
            <p class="xsmall muted">The registration test under section 190A is a threshold, not a verdict. It says
              nothing about whether native title exists over the area, and nothing about the people who brought the
              application. An application that did not meet it can be amended and tested again.</p>
          </div>

          <div class="detail-block">
            <h3>The matter</h3>
            <div class="detail-grid">
              <div class="detail-field">
                <div class="detail-label">Tribunal file number</div>
                <div class="mono">${esc(m.id)}</div>
              </div>
              <div class="detail-field">
                <div class="detail-label">Application type</div>
                <div>${esc(type ?? 'Not recorded')}</div>
              </div>
              <div class="detail-field">
                <div class="detail-label">Jurisdiction</div>
                <div>${esc(jn)}</div>
              </div>
              <div class="detail-field">
                <div class="detail-label">Legal or representative body</div>
                <div class="verbatim">${esc(m.representative ?? 'Not recorded')}</div>
              </div>
              <div class="detail-field">
                <div class="detail-label">Area of the application</div>
                <div class="mono">${esc(km2(m.areaSqKm, { exact: true }))}</div>
                <div class="xsmall muted">The area applied for — not an area over which anything has been found.</div>
              </div>
              <div class="detail-field">
                <div class="detail-label">Sea country</div>
                <div>${m.sea ? 'The application includes sea country' : 'Land only'}</div>
              </div>
            </div>
          </div>

          <div class="note small">Nothing has been decided about this area. A live application is a claim put to
            the Court, not a finding: it tells you what is being asked, not what will be answered. If and when the
            Court makes a ${glossTerm('determination', 'determination')}, this matter leaves these two registers
            and joins the National Native Title Register.</div>

          <div class="note legal xsmall">Areas shown here are indicative only. They are not cadastral or legal
            boundaries, they cannot be used to establish rights over any particular parcel of land or water, and
            nothing here is legal advice. The National Native Title Tribunal’s own registers are the authoritative
            record.</div>
        `;

        panel.append(cardHead, body);
      },
      onClose() {
        setParams({ matter: null });
      },
    });
    setParams({ matter: m.id });
  }

  /* ────────────────────────────── first paint ──────────────────────────── */

  syncChips();
  redraw();

  // A rotation, or a resized viewport, changes the pixel width the strip should
  // be measured at. The listener removes itself once the view it belongs to has
  // left the document, so navigating away cannot leak it.
  let everMounted = false;
  const onResize = () => {
    if (document.contains(frame)) everMounted = true;
    else if (everMounted) { removeEventListener('resize', onResize); return; }
    if (Math.abs(Math.max(MIN_WIDTH, containerWidth()) - lastWidth) < 8) return;
    redraw();
  };
  addEventListener('resize', onResize);

  const deepLink = currentRoute().params.get('matter');
  if (deepLink) {
    const target = matters.find((m) => m.id === deepLink);
    if (target) openRecord(target);
  }
}
